/**
 * Application Routes
 * Defines all routes and route protection
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import LandingPage from '@/pages/LandingPage';
import DashboardPage from '@/pages/DashboardPage';
import FlightEntryPage from '@/pages/FlightEntryPage';
import FlightEditPage from '@/pages/FlightEditPage';
import { TripsPage } from '@/pages/TripsPage';
import { TripDetailPage } from '@/pages/TripDetailPage';
import { FlightWizardPage } from '@/pages/FlightWizardPage';
import PaySummaryPage from '@/pages/PaySummaryPage';
import AdminPage from '@/pages/AdminPage';
import MelSearchPage from '@/pages/MelSearchPage';
import ActiveMelsPage from '@/pages/ActiveMelsPage';
import MelDocumentsPage from '@/pages/MelDocumentsPage';
import { LogbookPage } from '@/pages/LogbookPage';
import { TripInfoPage } from '@/pages/TripInfoPage';
import OpsPage from '@/pages/OpsPage';
import ExpenseAuditPage from '@/pages/ExpenseAuditPage';
import ApprovalsPage from '@/pages/ApprovalsPage';
import SafetyReportsPage from '@/pages/SafetyReportsPage';
import DutyTimePage from '@/pages/DutyTimePage';
import { useAuth } from '@/contexts/AuthContext';

/** Redirect to role-appropriate home page, or landing page if not logged in */
function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <LandingPage />;
  const target = user.role === 'admin' ? '/ops' : '/dashboard';
  return <Navigate to={target} replace />;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Operations Dashboard */}
          <Route
            path="/ops"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <OpsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Expense Audit */}
          <Route
            path="/expense-audit"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ExpenseAuditPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Approvals */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ApprovalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Duty Time Compliance */}
          <Route
            path="/duty-time"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <DutyTimePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Safety Reports */}
          <Route
            path="/safety-reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <SafetyReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Flight routes */}
          <Route
            path="/flights"
            element={
              <ProtectedRoute>
                <Layout>
                  <FlightEntryPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/flights/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <FlightEditPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Trip routes */}
          <Route
            path="/trips"
            element={
              <ProtectedRoute>
                <Layout>
                  <TripsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/trips/:id/wizard"
            element={
              <ProtectedRoute>
                <FlightWizardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/trips/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <TripDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Logbook */}
          <Route
            path="/logbook"
            element={
              <ProtectedRoute>
                <Layout>
                  <LogbookPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Trip info / briefing */}
          <Route
            path="/trips/:id/info"
            element={
              <ProtectedRoute>
                <Layout>
                  <TripInfoPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pay"
            element={
              <ProtectedRoute allowedRoles={['pilot', 'admin']}>
                <Layout>
                  <PaySummaryPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <AdminPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* MEL routes */}
          <Route
            path="/mel/search"
            element={
              <ProtectedRoute>
                <Layout>
                  <MelSearchPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mel/active"
            element={
              <ProtectedRoute>
                <Layout>
                  <ActiveMelsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mel/documents"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <MelDocumentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Default redirect — role-aware */}
          <Route path="/" element={<HomeRedirect />} />

          {/* 404 Not Found */}
          <Route
            path="*"
            element={
              <div
                className="min-h-screen flex items-center justify-center"
                style={{ backgroundColor: 'var(--bg-primary)' }}
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-foreground mb-4">404</h1>
                  <p className="text-foreground/60 mb-6">Page not found</p>
                  <a
                    href="/dashboard"
                    className="text-primary hover:text-primary-hover underline"
                  >
                    Return to Dashboard
                  </a>
                </div>
              </div>
            }
          />
            </Routes>
            <ReactQueryDevtools initialIsOpen={false} />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
